import React, { useCallback, useEffect, useState } from 'react';
import AiTag from './AiTag';
import * as XLSX from 'xlsx';
import { api } from '../lib/auth';
import { Button, Input, Textarea, Select, Card, ModalShell, Pill } from '../lib/ui';
import { parseImportRows, withProtocol, buildSocialUrl } from '../lib/importLeads';
import { MAX_FEATURED_PRODUCTS, FALLBACK_PHOTOS, photosTableHtml, useFeaturablePhotos } from '../lib/productPhotos';
import { useSavedTemplate } from '../lib/useSavedTemplate';
import { buildLeadsExport, leadsExportFilename } from '../lib/outreachDrafts';
import OutreachDraftsImport from './OutreachDraftsImport';

const ipcRenderer = window.process?.type === 'renderer' ? window.require('electron').ipcRenderer : null;

const label = {
  display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--muted)',
  textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6,
};

const STATUSES = ['new', 'contacted', 'responded', 'converted', 'declined'];
const STATUS_TONE = { new: 'warn', contacted: 'blue', responded: 'blue', converted: 'ok', declined: 'bad' };
const STATUS_LABEL = { new: 'New', contacted: 'Contacted', responded: 'Responded', converted: 'Converted', declined: 'Declined' };

const BLANK_LEAD = { businessName: '', contactName: '', email: '', phone: '', website: '', location: '' };
const SOCIAL_PLATFORMS = ['instagram', 'facebook', 'tiktok', 'other'];
const BLANK_SOCIAL_ROW = { platform: 'instagram', handle: '' };

// ── Starter pitch template ────────────────────────────────────────────────────
// Written to read like an actual email from Michael, not a marketing send:
// short paragraphs, no bullet checklist, no CTA button, plain sign-off.
// Replies go to the mailbox Michael actually reads (an older domain he's
// kept using), not the general site contact address.
const REPLY_TO_EMAIL = 'mwt@teardrophydroponics.co.uk';
const DEFAULT_PITCH_SUBJECT = 'Fresh herbs & salad leaves, grown up the road in Hockley';

// Editing the starter template: it's stored as a normal saved template (same
// /email/templates collection the Campaigns tab already uses, under this
// reserved name) so it can be overwritten from the UI without any new
// backend storage. Until one's been saved, "Use starter template" falls
// back to the hardcoded copy below.
const STARTER_TEMPLATE_NAME = 'Outreach starter template';
// Reuses the same template collection for a canned social-DM message —
// there's no field for it to auto-fill (the "notes" box on the social-log
// form doubles as the message you actually send, since this app only logs
// social outreach rather than sending it).
const SOCIAL_TEMPLATE_NAME = 'Outreach starter template — social message';
const DEFAULT_SOCIAL_MESSAGE = "Hi! I run a small hydroponic farm just outside Hockley - herbs, salad leaves and microgreens, grown indoors and cut to order. Thought it might be worth a try for you - happy to drop off a sample if you're up for it. Let me know!";

function buildPitchHtml(photos) {
  return `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<style>
  body { font-family: "DM Sans", Helvetica Neue, Helvetica, Arial, sans-serif; background: #f5f7f5; margin: 0; padding: 20px 0; color: #111a14; }
  .wrap { max-width: 540px; margin: 0 auto; background: #fff; border-radius: 12px; overflow: hidden; border: 1px solid #d4e0d4; }
  .bd { padding: 32px 36px; }
  p { font-size: 15px; line-height: 1.7; color: #2a3b32; margin: 0 0 16px; }
  .photos { width: 100%; border-collapse: separate; border-spacing: 6px 0; margin: 4px 0 6px; }
  .photos td { width: 33.33%; }
  .photos img { width: 100%; height: 100px; object-fit: cover; border-radius: 8px; display: block; }
  .caption { font-size: 12px; color: #5e7265; margin: 0 0 20px; }
  .sig { margin-top: 22px; font-size: 15px; color: #111a14; line-height: 1.6; }
</style>
</head>
<body>
  <div class="wrap">
    <div class="bd">
      <p>Hi {{name}},</p>

      <p>I run a small hydroponic farm just outside Hockley — herbs, salad leaves and microgreens, grown indoors so it's the same quality whatever the weather's doing outside. Everything's cut to order, usually within a few hours of going out.</p>

      ${photosTableHtml(photos.length ? photos : FALLBACK_PHOTOS)}
      <p class="caption">A few things growing at the moment.</p>

      <p>Thought it might be worth a try for you — no pesticides, properly fresh, and I'm only a few miles away so getting it to you is easy. Happy to drop off a sample if you want to see for yourself, no obligation either way.</p>

      <p>Just reply to this email if you fancy giving it a go, or if you've got any questions.</p>

      <div class="sig">
        Cheers,<br />
        Michael<br />
        07541 264513<br />
        TearDrop Hydro, Hockley
      </div>
    </div>
  </div>
</body>
</html>`;
}

function Banner({ msg, onClose }) {
  if (!msg) return null;
  const isErr = msg.startsWith('Error');
  return (
    <div style={{
      padding: '12px 16px', borderRadius: 'var(--r-lg)', marginBottom: 16,
      background: isErr ? '#fef2f2' : '#f0fdf4',
      border: `1px solid ${isErr ? '#fca5a5' : '#86efac'}`,
      color: isErr ? '#991b1b' : '#166534',
      display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 14,
    }}>
      <span>{msg}</span>
      <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: 18, lineHeight: 1 }}>×</button>
    </div>
  );
}

function interactionLine(i) {
  switch (i.type) {
    case 'note':          return { title: 'Note', body: i.text };
    case 'email':         return { title: 'Email sent', body: `"${i.subject}" → ${i.to}` };
    case 'social':        return { title: `Social · ${i.platform}`, body: `${i.direction === 'inbound' ? 'From' : 'To'} ${i.handle}${i.notes ? ` — ${i.notes}` : ''}` };
    case 'status_change': return { title: 'Status changed', body: `${STATUS_LABEL[i.fromStatus] || i.fromStatus} → ${STATUS_LABEL[i.toStatus] || i.toStatus}` };
    default:               return { title: i.type, body: '' };
  }
}

export default function OutreachTab() {
  const [leads, setLeads]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [banner, setBanner]       = useState('');
  const flash = msg => { setBanner(msg); setTimeout(() => setBanner(''), 6000); };

  const [selectedId, setSelectedId] = useState(null);
  const selectedLead = leads.find(l => l._id === selectedId) || null;

  const [showAdd, setShowAdd]     = useState(false);
  const [addForm, setAddForm]     = useState(BLANK_LEAD);
  const [saving, setSaving]       = useState(false);

  const [noteText, setNoteText]   = useState('');
  const [savingNote, setSavingNote] = useState(false);

  const [socialForm, setSocialForm] = useState({ platform: 'instagram', handle: '', direction: 'outbound', notes: '' });
  const [savingSocial, setSavingSocial] = useState(false);

  const [pitchSubject, setPitchSubject] = useState('');
  const [pitchHtml, setPitchHtml]       = useState('');
  const [sendingPitch, setSendingPitch] = useState(false);

  // Which lead's saved draft is in the composer right now (null if none).
  // Sending while it's set clears that draft; it also lets the composer warn
  // when the message on screen was written for a different business.
  const [draftLoadedFor, setDraftLoadedFor] = useState(null);
  const [savingDraft, setSavingDraft]       = useState(false);
  const [showDraftImport, setShowDraftImport] = useState(false);

  const [featuredIds, setFeaturedIds] = useState([]);
  const pickablePhotos = useFeaturablePhotos();

  const { template: starterTemplate, saving: savingStarter, save: saveStarterTemplateRaw } = useSavedTemplate(STARTER_TEMPLATE_NAME);
  const { template: socialTemplate, saving: savingSocialTemplate, save: saveSocialTemplateRaw } = useSavedTemplate(SOCIAL_TEMPLATE_NAME);

  const loadLeads = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/leads');
      setLeads(data);
    } catch (e) {
      flash(`Error: ${e.response?.data?.error || e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadLeads(); }, [loadLeads]);

  // ── Open a lead ─────────────────────────────────────────────────────────────
  // A lead's saved draft drops straight into the composer when the composer is
  // empty or only holds another lead's untouched draft. Anything you've typed
  // or edited is never overwritten. And another business's untouched draft is
  // never left behind on a lead it wasn't written for.
  const openLead = (l) => {
    const prev = draftLoadedFor ? leads.find(x => x._id === draftLoadedFor) : null;
    const composerEmpty = !pitchSubject.trim() && !pitchHtml.trim();
    const composerIsUntouchedDraft = !!prev?.draft
      && pitchSubject === prev.draft.subject && pitchHtml === prev.draft.html;

    setSelectedId(l._id);
    if (!composerEmpty && !composerIsUntouchedDraft) return;
    if (l.draft) {
      setPitchSubject(l.draft.subject);
      setPitchHtml(l.draft.html);
      setDraftLoadedFor(l._id);
    } else if (composerIsUntouchedDraft) {
      setPitchSubject('');
      setPitchHtml('');
      setDraftLoadedFor(null);
    }
  };

  // ── Export for drafting ─────────────────────────────────────────────────────
  // Desktop app: written to .outreach/leads.json in this repo, where Claude
  // reads it. Browser: downloaded as a file instead.
  const exportForDrafting = async () => {
    const now = new Date();
    const data = buildLeadsExport(leads, now);
    if (ipcRenderer) {
      const res = await ipcRenderer.invoke('outreach-leads-write', data);
      if (res?.error) return flash(`Error: Couldn't write the export (${res.error})`);
      return flash(`Exported ${data.summary.total} leads to ${res.path}`);
    }
    const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = leadsExportFilename(now);
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    flash(`Exported ${data.summary.total} leads`);
  };

  const toggleFeatured = (id) => {
    setFeaturedIds(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      if (prev.length >= MAX_FEATURED_PRODUCTS) {
        flash(`You can feature up to ${MAX_FEATURED_PRODUCTS} products at a time`);
        return prev;
      }
      return [...prev, id];
    });
  };

  const [copiedPhotoId, setCopiedPhotoId] = useState(null);
  const copyPhotoUrl = (e, p) => {
    e.stopPropagation(); // don't also toggle "featured" for this thumbnail
    navigator.clipboard.writeText(p.url).then(() => {
      setCopiedPhotoId(p.id);
      setTimeout(() => setCopiedPhotoId(null), 1500);
    });
  };

  // ── Create ──────────────────────────────────────────────────────────────────
  const createLead = async () => {
    if (!addForm.businessName.trim()) return flash('Error: Business name is required');
    setSaving(true);
    try {
      await api.post('/leads', addForm);
      await loadLeads();
      setShowAdd(false);
      setAddForm(BLANK_LEAD);
      flash(`Added "${addForm.businessName.trim()}"`);
    } catch (e) {
      flash(`Error: ${e.response?.data?.error || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  // ── Edit ────────────────────────────────────────────────────────────────────
  const [showEdit, setShowEdit] = useState(false);
  const [editForm, setEditForm] = useState(BLANK_LEAD);
  const [editSocials, setEditSocials] = useState([]);
  const [savingEdit, setSavingEdit] = useState(false);

  const openEdit = () => {
    if (!selectedLead) return;
    const { businessName, contactName, email, phone, website, location } = selectedLead;
    setEditForm({ businessName, contactName: contactName || '', email: email || '', phone: phone || '', website: website || '', location: location || '' });
    setEditSocials((selectedLead.socials || []).map(s => ({ platform: s.platform, handle: s.handle })));
    setShowEdit(true);
  };

  const addEditSocialRow = () => setEditSocials(prev => [...prev, { ...BLANK_SOCIAL_ROW }]);
  const removeEditSocialRow = (idx) => setEditSocials(prev => prev.filter((_, i) => i !== idx));
  const updateEditSocialRow = (idx, patch) => setEditSocials(prev => prev.map((s, i) => i === idx ? { ...s, ...patch } : s));

  const saveEdit = async () => {
    if (!editForm.businessName.trim()) return flash('Error: Business name is required');
    setSavingEdit(true);
    try {
      const socials = editSocials
        .filter(s => s.handle.trim())
        .map(s => ({ platform: s.platform, handle: s.handle.trim(), url: buildSocialUrl(s.platform, s.handle) }));
      const { data } = await api.patch(`/leads/${selectedLead._id}`, { ...editForm, socials });
      setLeads(prev => prev.map(l => l._id === data._id ? data : l));
      setShowEdit(false);
      flash('Lead updated');
    } catch (e) {
      flash(`Error: ${e.response?.data?.error || e.message}`);
    } finally {
      setSavingEdit(false);
    }
  };

  // ── Import from spreadsheet ──────────────────────────────────────────────────
  const [showImport, setShowImport]     = useState(false);
  const [importRows, setImportRows]     = useState(null);  // null = no file parsed yet
  const [importFileName, setImportFileName] = useState('');
  const [importing, setImporting]       = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importResult, setImportResult] = useState(null);

  const resetImport = () => {
    setImportRows(null);
    setImportFileName('');
    setImportProgress(0);
    setImportResult(null);
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    resetImport();
    setImportFileName(file.name);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const raw = XLSX.utils.sheet_to_json(sheet, { defval: '' });

      const { rows, error } = parseImportRows(raw, leads);
      if (error) return flash(`Error: ${error}`);

      setImportRows(rows);
    } catch (err) {
      flash(`Error: Couldn't read that file (${err.message})`);
    }
  };

  const toggleImportRow = (idx) => {
    setImportRows(prev => prev.map((r, i) => i === idx ? { ...r, include: !r.include } : r));
  };

  const runImport = async () => {
    const toImport = importRows.filter(r => r.include);
    if (!toImport.length) return flash('Error: Nothing selected to import');
    setImporting(true);
    let created = 0, failed = 0;
    for (let i = 0; i < toImport.length; i++) {
      const row = toImport[i];
      try {
        const { data: lead } = await api.post('/leads', {
          businessName: row.businessName, email: row.email, phone: row.phone,
          website: row.website, location: row.location, tags: row.tags, socials: row.socials,
        });
        if (row.importNote) {
          await api.post(`/leads/${lead._id}/notes`, { text: row.importNote });
        }
        created++;
      } catch {
        failed++;
      }
      setImportProgress(i + 1);
    }
    setImporting(false);
    setImportResult({ created, failed });
    await loadLeads();
  };

  // ── Status change ───────────────────────────────────────────────────────────
  const setStatus = async (status) => {
    if (!selectedLead || status === selectedLead.status) return;
    try {
      const { data } = await api.patch(`/leads/${selectedLead._id}`, { status });
      setLeads(prev => prev.map(l => l._id === data._id ? data : l));
    } catch (e) {
      flash(`Error: ${e.response?.data?.error || e.message}`);
    }
  };

  // ── Extra emails ──────────────────────────────────────────────────────────────
  // One "primary" email (used for sending) plus a growing list of other
  // candidates found along the way — any of them can be promoted to primary.
  const [newEmailInput, setNewEmailInput] = useState('');

  const patchLead = async (fields) => {
    const { data } = await api.patch(`/leads/${selectedLead._id}`, fields);
    setLeads(prev => prev.map(l => l._id === data._id ? data : l));
    return data;
  };

  const addOtherEmail = async () => {
    const email = newEmailInput.trim().toLowerCase();
    if (!email) return;
    if (email === selectedLead.email || (selectedLead.otherEmails || []).includes(email)) {
      flash('Error: That email is already on this lead');
      return;
    }
    try {
      await patchLead({ otherEmails: [...(selectedLead.otherEmails || []), email] });
      setNewEmailInput('');
    } catch (e) {
      flash(`Error: ${e.response?.data?.error || e.message}`);
    }
  };

  const removeOtherEmail = async (email) => {
    try {
      await patchLead({ otherEmails: (selectedLead.otherEmails || []).filter(e => e !== email) });
    } catch (e) {
      flash(`Error: ${e.response?.data?.error || e.message}`);
    }
  };

  const makeEmailPrimary = async (email) => {
    try {
      const others = [selectedLead.email, ...(selectedLead.otherEmails || []).filter(e => e !== email)].filter(Boolean);
      await patchLead({ email, otherEmails: others });
      flash(`${email} is now the primary email`);
    } catch (e) {
      flash(`Error: ${e.response?.data?.error || e.message}`);
    }
  };

  // ── Delete ──────────────────────────────────────────────────────────────────
  const deleteLead = async () => {
    if (!selectedLead) return;
    if (!window.confirm(`Delete "${selectedLead.businessName}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/leads/${selectedLead._id}`);
      setSelectedId(null);
      await loadLeads();
      flash('Lead deleted');
    } catch (e) {
      flash(`Error: ${e.response?.data?.error || e.message}`);
    }
  };

  // ── Note ────────────────────────────────────────────────────────────────────
  const addNote = async () => {
    if (!noteText.trim() || !selectedLead) return;
    setSavingNote(true);
    try {
      const { data } = await api.post(`/leads/${selectedLead._id}/notes`, { text: noteText.trim() });
      setLeads(prev => prev.map(l => l._id === data._id ? data : l));
      setNoteText('');
    } catch (e) {
      flash(`Error: ${e.response?.data?.error || e.message}`);
    } finally {
      setSavingNote(false);
    }
  };

  // ── Social log ──────────────────────────────────────────────────────────────
  const logSocial = async () => {
    if (!socialForm.handle.trim() || !selectedLead) return flash('Error: Enter a handle/username');
    setSavingSocial(true);
    try {
      const { data } = await api.post(`/leads/${selectedLead._id}/social`, socialForm);
      setLeads(prev => prev.map(l => l._id === data._id ? data : l));
      setSocialForm({ platform: socialForm.platform, handle: '', direction: 'outbound', notes: '' });
      flash('Social outreach logged');
    } catch (e) {
      flash(`Error: ${e.response?.data?.error || e.message}`);
    } finally {
      setSavingSocial(false);
    }
  };

  const useSocialTemplate = () => {
    if (socialForm.notes.trim() && !window.confirm('Replace the current message with the starter template?')) return;
    setSocialForm(f => ({ ...f, notes: socialTemplate ? (socialTemplate.html || '') : DEFAULT_SOCIAL_MESSAGE }));
  };

  const saveSocialTemplate = async () => {
    if (!socialForm.notes.trim()) return flash('Error: Write a message before saving it as the starter template');
    try {
      await saveSocialTemplateRaw('', socialForm.notes);
      flash('Saved — "Use starter template" will load this from now on');
    } catch (e) {
      flash(`Error: ${e.response?.data?.error || e.message}`);
    }
  };

  // ── Send pitch email ────────────────────────────────────────────────────────
  const sendPitch = async () => {
    if (!selectedLead) return;
    if (!selectedLead.email) return flash('Error: This lead has no email address');
    if (!pitchSubject.trim() || !pitchHtml.trim()) return flash('Error: Add a subject and message body first');
    if (!window.confirm(`Send "${pitchSubject.trim()}" to ${selectedLead.email}?`)) return;
    setSendingPitch(true);
    try {
      const { data } = await api.post(`/leads/${selectedLead._id}/send-email`, {
        subject: pitchSubject.trim(), html: pitchHtml, replyTo: REPLY_TO_EMAIL,
        clearDraft: draftLoadedFor === selectedLead._id,
      });
      setLeads(prev => prev.map(l => l._id === data.lead._id ? data.lead : l));
      setPitchSubject('');
      setPitchHtml('');
      setDraftLoadedFor(null);
      flash(`Sent to ${selectedLead.email}`);
    } catch (e) {
      flash(`Error: ${e.response?.data?.error || e.message}`);
    } finally {
      setSendingPitch(false);
    }
  };

  // ── Saved draft on this lead ─────────────────────────────────────────────────
  const loadDraft = () => {
    const d = selectedLead?.draft;
    if (!d) return;
    const holdsSomethingElse = (pitchSubject.trim() || pitchHtml.trim())
      && !(pitchSubject === d.subject && pitchHtml === d.html);
    if (holdsSomethingElse && !window.confirm('Replace the current subject and message with the saved draft?')) return;
    setPitchSubject(d.subject);
    setPitchHtml(d.html);
    setDraftLoadedFor(selectedLead._id);
  };

  const saveDraft = async () => {
    if (!pitchSubject.trim() || !pitchHtml.trim()) return flash('Error: Write a subject and message before saving a draft');
    setSavingDraft(true);
    try {
      const { data } = await api.put(`/leads/${selectedLead._id}/draft`, {
        subject: pitchSubject.trim(), html: pitchHtml, notes: selectedLead.draft?.notes || '',
      });
      setLeads(prev => prev.map(l => l._id === data._id ? data : l));
      setPitchSubject(data.draft.subject);
      setDraftLoadedFor(data._id);
      flash('Draft saved on this lead');
    } catch (e) {
      flash(`Error: ${e.response?.data?.error || e.message}`);
    } finally {
      setSavingDraft(false);
    }
  };

  const discardDraft = async () => {
    const d = selectedLead?.draft;
    if (!d) return;
    if (!window.confirm(`Discard the saved draft for "${selectedLead.businessName}"? This cannot be undone.`)) return;
    try {
      const { data } = await api.delete(`/leads/${selectedLead._id}/draft`);
      setLeads(prev => prev.map(l => l._id === data._id ? data : l));
      if (draftLoadedFor === data._id) {
        // Clear the composer too, unless it's been edited since loading
        if (pitchSubject === d.subject && pitchHtml === d.html) {
          setPitchSubject('');
          setPitchHtml('');
        }
        setDraftLoadedFor(null);
      }
      flash('Draft discarded');
    } catch (e) {
      flash(`Error: ${e.response?.data?.error || e.message}`);
    }
  };

  const useStarterTemplate = () => {
    if ((pitchSubject.trim() || pitchHtml.trim()) && !window.confirm('Replace the current subject and message with the starter template?')) return;
    if (starterTemplate) {
      setPitchSubject(starterTemplate.subject || '');
      setPitchHtml(starterTemplate.html || '');
      return;
    }
    const photos = featuredIds
      .map(id => pickablePhotos.find(p => p.id === id))
      .filter(Boolean)
      .map(p => ({ url: p.url, alt: p.name }));
    setPitchSubject(DEFAULT_PITCH_SUBJECT);
    setPitchHtml(buildPitchHtml(photos));
  };

  const saveStarterTemplate = async () => {
    if (!pitchSubject.trim() || !pitchHtml.trim()) return flash('Error: Write a subject and message before saving it as the starter template');
    try {
      await saveStarterTemplateRaw(pitchSubject, pitchHtml);
      flash('Saved — "Use starter template" will load this from now on');
    } catch (e) {
      flash(`Error: ${e.response?.data?.error || e.message}`);
    }
  };

  // ── Derived list ────────────────────────────────────────────────────────────
  const q = search.trim().toLowerCase();
  const visibleLeads = leads.filter(l => {
    if (statusFilter === 'drafts') { if (!l.draft) return false; }
    else if (statusFilter !== 'all' && l.status !== statusFilter) return false;
    if (!q) return true;
    return [l.businessName, l.contactName, l.email].some(v => (v || '').toLowerCase().includes(q));
  });

  const counts = {
    all: leads.length,
    new: leads.filter(l => l.status === 'new').length,
    contacted: leads.filter(l => l.status === 'contacted').length,
    responded: leads.filter(l => l.status === 'responded').length,
    converted: leads.filter(l => l.status === 'converted').length,
    declined: leads.filter(l => l.status === 'declined').length,
    drafts: leads.filter(l => l.draft).length,
  };

  const previewHtml = pitchHtml.replace(/\{\{name\}\}/g, selectedLead?.contactName || 'there').replace(/\{\{email\}\}/g, selectedLead?.email || '');

  // ── Detail view ─────────────────────────────────────────────────────────────
  if (selectedLead) {
    const timeline = [...selectedLead.interactions].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    return (
      <div>
        <Banner msg={banner} onClose={() => setBanner('')} />
        <Button variant="ghost" onClick={() => setSelectedId(null)} style={{ marginBottom: 16 }}>← Back to leads</Button>

        <Card style={{ padding: '20px 24px', marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <h3 style={{ margin: '0 0 4px', color: 'var(--brand,#113449)', fontSize: 20 }}>{selectedLead.businessName}<AiTag record={selectedLead} /></h3>
              <div style={{ color: 'var(--muted)', fontSize: 13 }}>
                {selectedLead.contactName && <span>{selectedLead.contactName} · </span>}
                {selectedLead.phone && <span>{selectedLead.phone} · </span>}
                {selectedLead.location || ''}
              </div>

              <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
                {selectedLead.email ? (
                  <span style={{ fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    {selectedLead.email}
                    <Pill tone="ok">Primary</Pill>
                  </span>
                ) : (
                  <span style={{ fontSize: 13, color: 'var(--muted)' }}>No email on file</span>
                )}
                {(selectedLead.otherEmails || []).map((e, i) => (
                  <span key={i} style={{ fontSize: 13, color: 'var(--muted)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    {e}
                    <button onClick={() => makeEmailPrimary(e)} style={{ border: 'none', background: 'none', color: 'var(--brand-2)', fontSize: 11, fontWeight: 600, cursor: 'pointer', padding: 0 }}>Make primary</button>
                    <button onClick={() => removeOtherEmail(e)} title="Remove" style={{ border: 'none', background: 'none', color: 'var(--muted)', fontSize: 13, cursor: 'pointer', padding: 0 }}>×</button>
                  </span>
                ))}
              </div>
              <div style={{ marginTop: 6, display: 'flex', gap: 6 }}>
                <Input
                  style={{ maxWidth: 220, fontSize: 12, padding: '5px 8px' }}
                  type="email"
                  placeholder="Add another email…"
                  value={newEmailInput}
                  onChange={e => setNewEmailInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addOtherEmail()}
                />
                <Button variant="ghost" onClick={addOtherEmail} disabled={!newEmailInput.trim()}>+ Add</Button>
              </div>

              {(selectedLead.website || selectedLead.socials?.length > 0) && (
                <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                  {selectedLead.website && (
                    <a href={withProtocol(selectedLead.website)} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, fontWeight: 600, color: 'var(--brand-2)', textDecoration: 'none' }}>
                      {selectedLead.website.replace(/^https?:\/\//i, '')} ↗
                    </a>
                  )}
                  {(selectedLead.socials || []).map((s, i) => (
                    <a key={i} href={s.url || buildSocialUrl(s.platform, s.handle)} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, fontWeight: 600, color: 'var(--brand-2)', textDecoration: 'none', textTransform: 'capitalize' }}>
                      {s.platform}: {s.handle} ↗
                    </a>
                  ))}
                </div>
              )}

              {selectedLead.tags?.filter(t => t && t !== selectedLead.location).length > 0 && (
                <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {selectedLead.tags.filter(t => t && t !== selectedLead.location).map((t, i) => <Pill key={i}>{t}</Pill>)}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button variant="ghost" onClick={openEdit}>Edit</Button>
              <Button variant="danger" onClick={deleteLead}>Delete lead</Button>
            </div>
          </div>

          <div style={{ marginTop: 14, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {STATUSES.map(s => (
              <span key={s} onClick={() => setStatus(s)} style={{ cursor: 'pointer', opacity: s === selectedLead.status ? 1 : 0.45 }}>
                <Pill tone={STATUS_TONE[s]}>{STATUS_LABEL[s]}</Pill>
              </span>
            ))}
          </div>
        </Card>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          {/* Add note */}
          <Card style={{ padding: '18px 20px' }}>
            <h3 style={{ margin: '0 0 12px', fontSize: 15 }}>Add a note</h3>
            <Textarea style={{ height: 90, fontSize: 13 }} placeholder="What happened?" value={noteText} onChange={e => setNoteText(e.target.value)} />
            <Button variant="primary" style={{ marginTop: 10 }} onClick={addNote} disabled={savingNote || !noteText.trim()}>
              {savingNote ? 'Saving…' : 'Add note'}
            </Button>
          </Card>

          {/* Log social outreach */}
          <Card style={{ padding: '18px 20px' }}>
            <h3 style={{ margin: '0 0 12px', fontSize: 15 }}>Log social outreach</h3>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
              <Select style={{ width: 140 }} value={socialForm.platform} onChange={e => setSocialForm(f => ({ ...f, platform: e.target.value }))}>
                <option value="instagram">Instagram</option>
                <option value="facebook">Facebook</option>
                <option value="tiktok">TikTok</option>
                <option value="other">Other</option>
              </Select>
              <Input style={{ flex: 1, minWidth: 140 }} placeholder="@handle" value={socialForm.handle} onChange={e => setSocialForm(f => ({ ...f, handle: e.target.value }))} />
              <Select style={{ width: 120 }} value={socialForm.direction} onChange={e => setSocialForm(f => ({ ...f, direction: e.target.value }))}>
                <option value="outbound">Outbound</option>
                <option value="inbound">Inbound</option>
              </Select>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, flexWrap: 'wrap', gap: 6 }}>
              <label style={{ ...label, marginBottom: 0 }}>Message <span style={{ textTransform: 'none', fontWeight: 400 }}>(what you're sending them — copy into Instagram/Facebook, or use as a note)</span></label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 11, color: 'var(--muted)' }}>{socialTemplate ? 'saved version' : 'built-in default'}</span>
                <Button variant="ghost" onClick={useSocialTemplate}>Use starter template</Button>
                <Button variant="ghost" onClick={saveSocialTemplate} disabled={savingSocialTemplate || !socialForm.notes.trim()} title="Overwrite the starter template with what's currently in the box below">
                  {savingSocialTemplate ? 'Saving…' : 'Save as starter template'}
                </Button>
              </div>
            </div>
            <Textarea style={{ height: 120, fontSize: 13 }} placeholder="What you said (or plan to say)…" value={socialForm.notes} onChange={e => setSocialForm(f => ({ ...f, notes: e.target.value }))} />
            <Button variant="primary" style={{ marginTop: 10 }} onClick={logSocial} disabled={savingSocial || !socialForm.handle.trim()}>
              {savingSocial ? 'Saving…' : 'Log outreach'}
            </Button>
          </Card>
        </div>

        {/* Send pitch email */}
        <Card style={{ padding: '18px 20px', marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
            <h3 style={{ margin: 0, fontSize: 15 }}>
              Send an email
              {!selectedLead.email && <span style={{ color: 'var(--muted)', fontWeight: 400, marginLeft: 8, fontSize: 12 }}>(no email address on file)</span>}
            </h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 11, color: 'var(--muted)' }}>{starterTemplate ? 'using your saved version' : 'using the built-in default'}</span>
              <Button variant="ghost" onClick={useStarterTemplate}>Use starter template</Button>
              <Button variant="ghost" onClick={saveStarterTemplate} disabled={savingStarter || !pitchSubject.trim() || !pitchHtml.trim()} title="Overwrite the starter template with what's currently in the boxes below">
                {savingStarter ? 'Saving…' : 'Save as starter template'}
              </Button>
            </div>
          </div>

          {selectedLead.draft && (
            <div style={{ marginBottom: 16, padding: '12px 14px', border: '1px solid #c3e6cb', background: '#eef7f0', borderRadius: 'var(--r-md)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <div style={{ fontSize: 13 }}>
                  <strong style={{ color: '#1a6e35' }}>Draft ready</strong>
                  <span style={{ color: 'var(--muted)', marginLeft: 8 }}>
                    {draftLoadedFor === selectedLead._id ? 'loaded below' : 'not loaded'}
                    {selectedLead.draft.updatedAt && ` · saved ${new Date(selectedLead.draft.updatedAt).toLocaleString('en-GB')}`}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button variant="ghost" onClick={loadDraft}>{draftLoadedFor === selectedLead._id ? 'Reload draft' : 'Load draft'}</Button>
                  <Button variant="ghost" onClick={discardDraft}>Discard draft</Button>
                </div>
              </div>
              {selectedLead.draft.notes && (
                <div style={{ marginTop: 10 }}>
                  <span style={label}>Check before sending</span>
                  <div style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{selectedLead.draft.notes}</div>
                </div>
              )}
            </div>
          )}

          {draftLoadedFor && draftLoadedFor !== selectedLead._id && (pitchSubject.trim() || pitchHtml.trim()) && (
            <div style={{ marginBottom: 16, padding: '10px 14px', border: '1px solid #ffd591', background: '#fff7e6', borderRadius: 'var(--r-md)', fontSize: 13, color: '#8c6d1f' }}>
              The message below started as the draft for <strong>{leads.find(l => l._id === draftLoadedFor)?.businessName || 'another lead'}</strong>. Check it's right for {selectedLead.businessName} before sending.
            </div>
          )}

          <div style={{ marginBottom: 16 }}>
            <label style={label}>Feature products in the template <span style={{ textTransform: 'none', fontWeight: 400 }}>(optional, up to {MAX_FEATURED_PRODUCTS} — or click the link icon to copy a photo's URL and paste it into the message yourself)</span></label>
            {pickablePhotos.length === 0 ? (
              <p style={{ color: 'var(--muted)', fontSize: 12, margin: 0 }}>No product photos available to feature — the starter template will use generic sample photos instead.</p>
            ) : (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {pickablePhotos.map(p => {
                  const picked = featuredIds.includes(p.id);
                  return (
                    <div
                      key={p.id}
                      onClick={() => toggleFeatured(p.id)}
                      title={p.name}
                      style={{
                        width: 72, cursor: 'pointer', textAlign: 'center',
                        border: `2px solid ${picked ? 'var(--brand-2)' : 'var(--border)'}`,
                        borderRadius: 'var(--r-md)', padding: 4, background: picked ? 'var(--panel-2)' : 'var(--panel)',
                      }}
                    >
                      <div style={{ position: 'relative' }}>
                        <img src={p.url} alt="" style={{ width: '100%', height: 48, objectFit: 'cover', borderRadius: 6, display: 'block' }} />
                        <button
                          onClick={(e) => copyPhotoUrl(e, p)}
                          title="Copy image link"
                          style={{
                            position: 'absolute', top: 2, right: 2, width: 18, height: 18, padding: 0,
                            border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 10, lineHeight: '18px',
                            background: 'rgba(255,255,255,.9)', color: 'var(--brand-2)',
                          }}
                        >
                          {copiedPhotoId === p.id ? '✓' : '🔗'}
                        </button>
                      </div>
                      <div style={{ fontSize: 10, marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: picked ? 'var(--brand-2)' : 'var(--muted)', fontWeight: picked ? 700 : 400 }}>
                        {p.name}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <label style={label}>Subject</label>
              <Input style={{ marginBottom: 10 }} placeholder="e.g. Fresh, hyper-local herbs for your menu" value={pitchSubject} onChange={e => setPitchSubject(e.target.value)} />
              <label style={label}>Message <span style={{ textTransform: 'none', fontWeight: 400 }}>— use {'{{name}}'}</span></label>
              <Textarea style={{ height: 460, fontFamily: 'ui-monospace, monospace', fontSize: 12 }} value={pitchHtml} onChange={e => setPitchHtml(e.target.value)} placeholder="<p>Hi {{name}}, ...</p>" />
              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <Button variant="primary" onClick={sendPitch} disabled={sendingPitch || !selectedLead.email}>
                  {sendingPitch ? 'Sending…' : 'Send email'}
                </Button>
                <Button variant="ghost" onClick={saveDraft} disabled={savingDraft || !pitchSubject.trim() || !pitchHtml.trim()} title="Keep this subject and message on the lead to finish or send later">
                  {savingDraft ? 'Saving…' : 'Save as draft'}
                </Button>
              </div>
            </div>
            <div>
              <label style={label}>Preview</label>
              {pitchHtml ? (
                <iframe title="Pitch preview" srcDoc={previewHtml} sandbox="allow-same-origin" style={{ width: '100%', height: 460, border: '1px solid var(--border)', borderRadius: 'var(--r-md)', background: '#fff' }} />
              ) : (
                <div style={{ height: 460, border: '1px dashed var(--border)', borderRadius: 'var(--r-md)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--muted)', fontSize: 13 }}>
                  Preview appears here
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* Timeline */}
        <Card style={{ padding: '18px 20px' }}>
          <h3 style={{ margin: '0 0 12px', fontSize: 15 }}>History</h3>
          {timeline.length === 0 ? (
            <p style={{ color: 'var(--muted)', fontSize: 13 }}>No activity yet.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {timeline.map(i => {
                const { title, body } = interactionLine(i);
                return (
                  <div key={i._id} style={{ padding: '10px 14px', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', background: 'var(--panel-2)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--muted)', marginBottom: 4 }}>
                      <strong style={{ color: 'var(--text)' }}>{title}</strong>
                      <span>{new Date(i.createdAt).toLocaleString('en-GB')}</span>
                    </div>
                    {body && <div style={{ fontSize: 13 }}>{body}</div>}
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        {showEdit && (
          <ModalShell onClose={() => setShowEdit(false)} width={520} ariaLabel="Edit lead">
            <h2 style={{ margin: '0 0 16px', fontSize: 18 }}>Edit lead</h2>
            <div style={{ display: 'grid', gap: 12 }}>
              <div>
                <label style={label}>Business name</label>
                <Input value={editForm.businessName} onChange={e => setEditForm(f => ({ ...f, businessName: e.target.value }))} autoFocus />
              </div>
              <div>
                <label style={label}>Contact name</label>
                <Input value={editForm.contactName} onChange={e => setEditForm(f => ({ ...f, contactName: e.target.value }))} />
              </div>
              <div>
                <label style={label}>Email</label>
                <Input type="email" value={editForm.email} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} />
              </div>
              <div>
                <label style={label}>Phone</label>
                <Input value={editForm.phone} onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))} />
              </div>
              <div>
                <label style={label}>Website</label>
                <Input value={editForm.website} onChange={e => setEditForm(f => ({ ...f, website: e.target.value }))} placeholder="example.co.uk" />
              </div>
              <div>
                <label style={label}>Location</label>
                <Input value={editForm.location} onChange={e => setEditForm(f => ({ ...f, location: e.target.value }))} />
              </div>

              <div>
                <label style={label}>Social links</label>
                <div style={{ display: 'grid', gap: 8 }}>
                  {editSocials.map((s, i) => (
                    <div key={i} style={{ display: 'flex', gap: 8 }}>
                      <Select style={{ width: 130 }} value={s.platform} onChange={e => updateEditSocialRow(i, { platform: e.target.value })}>
                        {SOCIAL_PLATFORMS.map(p => <option key={p} value={p}>{p[0].toUpperCase() + p.slice(1)}</option>)}
                      </Select>
                      <Input style={{ flex: 1 }} placeholder="@handle or full URL" value={s.handle} onChange={e => updateEditSocialRow(i, { handle: e.target.value })} />
                      <Button variant="ghost" onClick={() => removeEditSocialRow(i)}>×</Button>
                    </div>
                  ))}
                </div>
                <Button variant="ghost" style={{ marginTop: 8 }} onClick={addEditSocialRow}>+ Add social link</Button>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
              <Button variant="muted" onClick={() => setShowEdit(false)}>Cancel</Button>
              <Button variant="primary" onClick={saveEdit} disabled={savingEdit || !editForm.businessName.trim()}>
                {savingEdit ? 'Saving…' : 'Save changes'}
              </Button>
            </div>
          </ModalShell>
        )}
      </div>
    );
  }

  // ── List view ───────────────────────────────────────────────────────────────
  return (
    <div>
      <Banner msg={banner} onClose={() => setBanner('')} />

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
        <Input style={{ maxWidth: 280 }} placeholder="Search leads…" value={search} onChange={e => setSearch(e.target.value)} />
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <span onClick={() => setStatusFilter('all')} style={{ cursor: 'pointer', opacity: statusFilter === 'all' ? 1 : 0.5 }}>
            <Pill>{`All (${counts.all})`}</Pill>
          </span>
          {STATUSES.map(s => (
            <span key={s} onClick={() => setStatusFilter(s)} style={{ cursor: 'pointer', opacity: statusFilter === s ? 1 : 0.5 }}>
              <Pill tone={STATUS_TONE[s]}>{`${STATUS_LABEL[s]} (${counts[s]})`}</Pill>
            </span>
          ))}
          {(counts.drafts > 0 || statusFilter === 'drafts') && (
            <span onClick={() => setStatusFilter('drafts')} style={{ cursor: 'pointer', opacity: statusFilter === 'drafts' ? 1 : 0.5 }}>
              <Pill tone="income">{`Drafts ready (${counts.drafts})`}</Pill>
            </span>
          )}
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button variant="ghost" onClick={exportForDrafting} disabled={loading || !leads.length} title="Save every lead to a file so emails can be drafted for them">Export for drafting</Button>
          <Button variant="ghost" onClick={() => setShowDraftImport(true)} disabled={loading}>Import drafts</Button>
          <Button variant="ghost" onClick={() => { resetImport(); setShowImport(true); }}>Import from spreadsheet</Button>
          <Button variant="primary" onClick={() => setShowAdd(true)}>+ Add lead</Button>
        </div>
      </div>

      {loading ? (
        <p style={{ color: 'var(--muted)' }}>Loading…</p>
      ) : visibleLeads.length === 0 ? (
        <Card style={{ padding: 24, textAlign: 'center', color: 'var(--muted)' }}>
          {leads.length === 0 ? 'No leads yet — add your first prospective customer above.' : 'No leads match this filter.'}
        </Card>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {visibleLeads.map(l => (
            <Card key={l._id} style={{ padding: '12px 16px', cursor: 'pointer' }} onClick={() => openLead(l)}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{l.businessName}<AiTag record={l} /></div>
                  <div style={{ fontSize: 12, color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {[l.contactName, l.email, l.location].filter(Boolean).join(' · ') || 'No details yet'}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  {l.draft && <Pill tone="income">Draft ready</Pill>}
                  <Pill tone={STATUS_TONE[l.status]}>{STATUS_LABEL[l.status]}</Pill>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {showAdd && (
        <ModalShell onClose={() => setShowAdd(false)} width={480} ariaLabel="Add lead">
          <h2 style={{ margin: '0 0 16px', fontSize: 18 }}>Add a lead</h2>
          <div style={{ display: 'grid', gap: 12 }}>
            <div>
              <label style={label}>Business name</label>
              <Input value={addForm.businessName} onChange={e => setAddForm(f => ({ ...f, businessName: e.target.value }))} autoFocus />
            </div>
            <div>
              <label style={label}>Contact name</label>
              <Input value={addForm.contactName} onChange={e => setAddForm(f => ({ ...f, contactName: e.target.value }))} />
            </div>
            <div>
              <label style={label}>Email</label>
              <Input type="email" value={addForm.email} onChange={e => setAddForm(f => ({ ...f, email: e.target.value }))} />
            </div>
            <div>
              <label style={label}>Phone</label>
              <Input value={addForm.phone} onChange={e => setAddForm(f => ({ ...f, phone: e.target.value }))} />
            </div>
            <div>
              <label style={label}>Website</label>
              <Input value={addForm.website} onChange={e => setAddForm(f => ({ ...f, website: e.target.value }))} placeholder="example.co.uk" />
            </div>
            <div>
              <label style={label}>Location</label>
              <Input value={addForm.location} onChange={e => setAddForm(f => ({ ...f, location: e.target.value }))} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
            <Button variant="muted" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button variant="primary" onClick={createLead} disabled={saving || !addForm.businessName.trim()}>
              {saving ? 'Adding…' : 'Add lead'}
            </Button>
          </div>
        </ModalShell>
      )}

      {showDraftImport && (
        <OutreachDraftsImport leads={leads} onClose={() => setShowDraftImport(false)} onImported={loadLeads} />
      )}

      {showImport && (
        <ModalShell onClose={() => setShowImport(false)} width={680} ariaLabel="Import leads from spreadsheet">
          <h2 style={{ margin: '0 0 4px', fontSize: 18 }}>Import leads from a spreadsheet</h2>
          <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--muted)' }}>
            Upload an .xlsx or .csv file — columns like Name, Email, Phone, Website, Facebook, Instagram, Area and Address are picked up automatically, in any order.
          </p>

          {!importResult && (
            <input type="file" accept=".xlsx,.xls,.csv" onChange={handleImportFile} style={{ marginBottom: 16 }} />
          )}

          {importRows && !importResult && (
            <>
              <p style={{ fontSize: 13, color: 'var(--muted)', margin: '0 0 10px' }}>
                <strong>{importFileName}</strong> — {importRows.length} row{importRows.length !== 1 ? 's' : ''} found,{' '}
                {importRows.filter(r => r.include).length} selected to import
                {importRows.some(r => r.duplicate) && ` (${importRows.filter(r => r.duplicate).length} look like duplicates of leads you already have, unchecked by default)`}.
              </p>
              <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', marginBottom: 16 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ position: 'sticky', top: 0, background: 'var(--panel-2)' }}>
                      <th style={{ padding: '8px 10px', textAlign: 'left', width: 28 }}></th>
                      <th style={{ padding: '8px 10px', textAlign: 'left' }}>Business</th>
                      <th style={{ padding: '8px 10px', textAlign: 'left' }}>Email</th>
                      <th style={{ padding: '8px 10px', textAlign: 'left' }}>Phone</th>
                      <th style={{ padding: '8px 10px', textAlign: 'left' }}>Area</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importRows.map((r, i) => (
                      <tr key={i} style={{ borderTop: '1px solid var(--border)', opacity: r.include ? 1 : 0.5 }}>
                        <td style={{ padding: '6px 10px' }}>
                          <input type="checkbox" checked={r.include} onChange={() => toggleImportRow(i)} />
                        </td>
                        <td style={{ padding: '6px 10px' }}>
                          {r.businessName}
                          {r.duplicate && <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--muted)' }}>(duplicate)</span>}
                        </td>
                        <td style={{ padding: '6px 10px', color: 'var(--muted)' }}>{r.email || '—'}</td>
                        <td style={{ padding: '6px 10px', color: 'var(--muted)' }}>{r.phone || '—'}</td>
                        <td style={{ padding: '6px 10px', color: 'var(--muted)' }}>{r.location || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {importing && (
            <p style={{ fontSize: 13, color: 'var(--muted)', margin: '0 0 16px' }}>
              Importing… {importProgress} / {importRows.filter(r => r.include).length}
            </p>
          )}

          {importResult && (
            <div style={{ padding: '12px 16px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 'var(--r-lg)', fontSize: 14, color: '#166534', marginBottom: 16 }}>
              Imported {importResult.created} lead{importResult.created !== 1 ? 's' : ''}
              {importResult.failed ? ` · ${importResult.failed} failed` : ''}.
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Button variant="muted" onClick={() => setShowImport(false)}>{importResult ? 'Close' : 'Cancel'}</Button>
            {importRows && !importResult && (
              <Button variant="primary" onClick={runImport} disabled={importing || !importRows.some(r => r.include)}>
                {importing ? 'Importing…' : `Import ${importRows.filter(r => r.include).length} lead${importRows.filter(r => r.include).length !== 1 ? 's' : ''}`}
              </Button>
            )}
          </div>
        </ModalShell>
      )}
    </div>
  );
}
